import { computed, makeObservable, observable } from 'mobx';

import { getWorkspaceContents, type WorkspaceItem, type WorkspaceParent } from 'api/workspace/endpoints/getWorkspaceContents';
import { extractErrorMessage } from 'helpers/extractErrorMessage';

// Folders per container are few, but page defensively and drain the cursor so a
// large container still shows every child (no per-level "more" in v1).
const CHILDREN_LIMIT = 200;

/**
 * A container node in the workspace tree: a zone (root) or a folder. Its `key`
 * IS its locator (`zone:<id>` / `folder:<id>`). `children` are materialized
 * (loaded folders, or a single loading placeholder while fetching) so a pure
 * `observer` that reads `roots` re-renders whenever expansion state, loaded
 * children, or loading flags change — TreeView is a plain controlled view and
 * would otherwise never re-render on a lazy load.
 */
export interface WorkspaceTreeNode {
  readonly key: string;
  readonly type: 'zone' | 'folder';
  readonly id: number;
  readonly name: string;
  readonly zoneId: number | null;
  readonly zoneName: string | null;
  // The underlying row, so the view can render the shared per-type label.
  readonly item: WorkspaceItem;
  readonly children: readonly WorkspaceTreeNode[];
  readonly hasLoadedChildren: boolean;
  // A synthetic child shown (with a spinner) while a node loads its folders.
  readonly isLoadingPlaceholder?: boolean;
}

const keyOf = (item: WorkspaceItem): WorkspaceParent => (item.type === 'zone' ? `zone:${item.id}` : `folder:${item.id}`);

const loadingPlaceholder = (parentKey: string): WorkspaceTreeNode => ({
  key: `${parentKey}:__loading__`,
  type: 'folder',
  id: -1,
  name: '',
  zoneId: null,
  zoneName: null,
  item: { type: 'folder', id: -1, name: '', description: null, zoneId: null, zoneName: null, folderId: null },
  children: [],
  hasLoadedChildren: true,
  isLoadingPlaceholder: true,
});

/**
 * The container tree (zones → folders), lazily loaded. A pure controlled data
 * source for `TreeView`: it owns roots, per-node loaded children (kept as the
 * full `WorkspaceItem` so the contents pane can render folders-first from the
 * same fetch), expansion, and loading. No react-router import. Unit-testable
 * headless.
 */
export class WorkspaceTreeModel {
  private _rootZones: readonly WorkspaceItem[] = [];
  // Shallow: the per-key value arrays are replaced wholesale (never mutated in
  // place), so entry membership is reactive but the arrays stay by-reference.
  private readonly _childrenByKey = new Map<string, readonly WorkspaceItem[]>();
  // Observable: drives the per-node loading placeholder (non-silent loads only).
  private _loadingKeys: ReadonlySet<string> = new Set();
  // Control-flow only (not rendered, so not observable): every in-flight
  // loadChildren, silent or not, so a silent reload also participates in
  // re-entrancy — `_loadingKeys` can't serve as the guard because silent loads
  // never enter it.
  private readonly _inFlight = new Set<string>();
  private _expandedIds: ReadonlySet<string> = new Set();
  private _rootsLoading = false;
  private _error: string | null = null;

  /** Resolves once the initial roots load settles (success or failure). For tests. */
  readonly loaded: Promise<void>;

  constructor() {
    makeObservable<WorkspaceTreeModel, '_rootZones' | '_childrenByKey' | '_loadingKeys' | '_expandedIds' | '_rootsLoading' | '_error'>(this, {
      _rootZones: observable.ref,
      _childrenByKey: observable.shallow,
      _loadingKeys: observable.ref,
      _expandedIds: observable.ref,
      _rootsLoading: observable,
      _error: observable,
      roots: computed,
    });
    this.loaded = this.loadRoots();
  }

  get error(): string | null {
    return this._error;
  }

  get expandedIds(): ReadonlySet<string> {
    return this._expandedIds;
  }

  // Read once per render by TreeView, but it recursively materializes the whole
  // tree — memoized so a re-render that didn't change zones/children/loading
  // reuses the built nodes.
  get roots(): readonly WorkspaceTreeNode[] {
    return this._rootZones.map((z) => this.materialize(z));
  }

  get rootsLoading(): boolean {
    return this._rootsLoading;
  }

  getId = (node: WorkspaceTreeNode): string => node.key;

  getChildren = (node: WorkspaceTreeNode): readonly WorkspaceTreeNode[] => node.children;

  // Expandable until loaded (so a lazy branch shows a chevron), then only if it
  // actually has subfolders. The loading placeholder itself is a leaf.
  hasChildren = (node: WorkspaceTreeNode): boolean => (node.isLoadingPlaceholder ? false : node.hasLoadedChildren ? node.children.length > 0 : true);

  /** The loaded child folders (as WorkspaceItem) of a container, for the contents pane. */
  childFolders = (key: string): readonly WorkspaceItem[] => this._childrenByKey.get(key) ?? [];

  /** Find a loaded zone/folder item by its locator key. */
  findItem = (key: string): WorkspaceItem | undefined => {
    const inRoots = this._rootZones.find((z) => keyOf(z) === key);
    if (inRoots) return inRoots;
    for (const children of this._childrenByKey.values()) {
      const hit = children.find((c) => keyOf(c) === key);
      if (hit) return hit;
    }
    return undefined;
  };

  toggle = (key: string): void => {
    const next = new Set(this._expandedIds);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
      void this.ensureLoaded(key as WorkspaceParent);
    }
    this._expandedIds = next;
  };

  /** Load (and cache) a container's child folders if not already loaded/loading. */
  ensureLoaded = async (key: WorkspaceParent): Promise<void> => {
    if (this._childrenByKey.has(key) || this._inFlight.has(key)) return;
    await this.loadChildren(key);
  };

  /** Silently re-fetch a container's child folders (after a folder create/delete). */
  reloadChildren = (key: WorkspaceParent): void => {
    if (this._inFlight.has(key)) return;
    void this.loadChildren(key, true);
  };

  /**
   * Expand the tree along a locator path (root→leaf), loading each level, so a
   * refresh can restore the tree expanded to the selected container.
   */
  expandPath = async (keys: readonly WorkspaceParent[]): Promise<void> => {
    const next = new Set(this._expandedIds);
    for (const key of keys) {
      next.add(key);
    }
    this._expandedIds = next;
    for (const key of keys) {
      await this.ensureLoaded(key);
    }
  };

  private materialize(item: WorkspaceItem): WorkspaceTreeNode {
    const key = keyOf(item);
    const childItems = this._childrenByKey.get(key);
    const loading = this._loadingKeys.has(key);
    const children = childItems !== undefined ? childItems.map((child) => this.materialize(child)) : loading ? [loadingPlaceholder(key)] : [];
    return {
      key,
      type: item.type as 'zone' | 'folder',
      id: item.id,
      name: item.name,
      zoneId: item.zoneId,
      zoneName: item.zoneName,
      item,
      children,
      hasLoadedChildren: childItems !== undefined,
    };
  }

  private setLoading(key: string, loading: boolean): void {
    const next = new Set(this._loadingKeys);
    if (loading) next.add(key);
    else next.delete(key);
    this._loadingKeys = next;
  }

  private async loadRoots(): Promise<void> {
    this._rootsLoading = true;
    this._error = null;
    try {
      const result = await getWorkspaceContents({ parent: 'root' });
      this._rootZones = result.items.filter((item) => item.type === 'zone');
    } catch (err) {
      this._error = extractErrorMessage(err, 'Failed to load zones');
    } finally {
      this._rootsLoading = false;
    }
  }

  private async loadChildren(parent: WorkspaceParent, silent = false): Promise<void> {
    if (this._inFlight.has(parent)) return;
    this._inFlight.add(parent);
    if (!silent) this.setLoading(parent, true);
    this._error = null;
    try {
      const folders: WorkspaceItem[] = [];
      let cursor: string | undefined;
      do {
        const result = await getWorkspaceContents({ parent, types: ['folder'], cursor, limit: CHILDREN_LIMIT });
        for (const item of result.items) {
          if (item.type === 'folder') folders.push(item);
        }
        cursor = result.nextCursor ?? undefined;
      } while (cursor);
      this._childrenByKey.set(parent, folders);
    } catch (err) {
      this._error = extractErrorMessage(err, 'Failed to load folders');
    } finally {
      this._inFlight.delete(parent);
      if (!silent) this.setLoading(parent, false);
    }
  }
}
